/**
 * A large demo fleet, for density and performance checks: ?mock=1&scale=big gives about 60
 * machines, 14 projects, 2,400 sessions, 300 checkouts and a full audit log (scale=huge: 240
 * machines and 10,000 sessions), and keeps adding
 * sessions and audit entries so live updates ("3 new, show") can be seen. Deterministic, so
 * screenshots are stable. scale=small (or no scale) is the normal demo.
 */
import type { ActionKind, AggregateState, AuditEntry, Checkout, Id, Machine, Pipeline, Project, Session, SessionStatus, Subagent } from '@orgus/protocol';
import { AUDIT_LIMIT } from './store.ts';
import type { MockSink } from './mock.ts';

export type MockScale = 'small' | 'big' | 'huge';

const SIZES: Record<Exclude<MockScale, 'small'>, { machines: number; sessions: number; checkouts: number }> = {
  big: { machines: 58, sessions: 2400, checkouts: 300 },
  huge: { machines: 240, sessions: 10_000, checkouts: 1200 },
};

const S = 1000;
const MIN = 60 * S;
const H = 60 * MIN;
const D = 24 * H;
const GB = 1024 ** 3;

function prng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** The scale the address asked for (remembered for the tab, like ?mock=1). */
export function mockScaleRequested(): MockScale {
  try {
    const q = new URLSearchParams(window.location.search).get('scale');
    if (q === 'big' || q === 'huge' || q === 'small') sessionStorage.setItem('fd.mockScale', q);
    const v = sessionStorage.getItem('fd.mockScale');
    return v === 'big' || v === 'huge' ? v : 'small';
  } catch {
    return 'small';
  }
}

const PREFIX = ['node', 'gpu', 'ci', 'mac', 'dev', 'edge', 'lab'];
const PROJECT_NAMES = ['payments-api', 'web-console', 'mobile-app', 'search-indexer', 'auth-gateway', 'data-pipeline', 'design-tokens', 'infra-terraform', 'ml-ranker', 'notifications'];
const TASKS = [
  'Fix the flaky checkout test',
  'Add pagination to the orders endpoint',
  'Migrate the settings page to the new form kit',
  'Find why p95 latency doubled after deploy',
  'Write the missing empty and error states',
  'Upgrade the router and fix the type errors',
  'Split the slow e2e shard in two',
  'Add an index for the audit query',
  'Review the retry logic in the webhook worker',
  'Port the CSV export to streaming',
  'Make the dark theme pass contrast checks',
  'Remove the dead feature flags',
  'Document the deploy runbook',
  'Reproduce the race in the session cache',
];
const ACTORS = ['owner', 'alice', 'bob', 'agent:triage', 'priya'];
const MODELS = ['claude-opus-5', 'claude-sonnet-5', 'claude-haiku-5-5'];
const KINDS: ActionKind[] = ['session.start', 'session.stop', 'job.run', 'job.cancel', 'worktree.remove', 'pipeline.pause', 'pipeline.resume', 'project.add'];

/** Add a big fleet on top of the normal demo state. */
export function scaleMockState(base: AggregateState, now: number, scale: Exclude<MockScale, 'small'> = 'big'): AggregateState {
  const size = SIZES[scale];
  const rand = prng(7);
  const pick = <T>(xs: readonly T[]): T => xs[Math.floor(rand() * xs.length)]!;

  const machines: Machine[] = [...base.machines];
  for (let i = 0; i < size.machines; i++) {
    const os = i % 7 === 3 ? 'macos' : i % 9 === 5 ? 'windows' : 'linux';
    const roll = rand();
    const online = roll > 0.09;
    const stale = online && roll < 0.14;
    const memTotal = (16 << (i % 4)) * GB;
    machines.push({
      id: `m-big-${i}`,
      name: `${PREFIX[i % PREFIX.length]}-${String(Math.floor(i / PREFIX.length) + 1).padStart(2, '0')}`,
      os,
      arch: os === 'macos' ? 'arm64' : 'x64',
      hostname: `${PREFIX[i % PREFIX.length]}-${i}.fleet.lan`,
      cpus: 8 << (i % 3),
      memTotal,
      memFree: memTotal * (0.15 + rand() * 0.7),
      load: online ? Math.min(0.98, rand() * rand() * 1.6) : 0,
      agentVersion: rand() < 0.12 ? '0.0.9' : '0.1.0',
      connectedAt: online ? now - Math.floor(rand() * 3 * D) : null,
      lastSeen: !online ? now - Math.floor(H + rand() * 2 * D) : stale ? now - 5 * MIN : now - Math.floor(rand() * 4 * S),
      online,
      labels: [pick(['ci', 'gpu', 'shared', 'team-a', 'team-b'])],
    });
  }

  const projects: Project[] = [...base.projects];
  for (let i = 0; i < PROJECT_NAMES.length; i++) {
    projects.push({ id: `p-big-${i}`, name: PROJECT_NAMES[i]!, remoteUrl: `git@github.com:acme/${PROJECT_NAMES[i]}.git`, defaultBranch: 'main', adapters: ['generic-git'] });
  }

  const checkouts: Checkout[] = [...base.checkouts];
  const onlineMachines = machines.filter((m) => m.online);
  for (let i = 0; i < size.checkouts; i++) {
    const p = pick(projects.slice(base.projects.length));
    const m = pick(machines.slice(base.machines.length));
    const wt = i % 3 !== 0;
    const branch = wt ? `feat/${p.name.split('-')[0]}-${i}` : 'main';
    checkouts.push({
      id: `c-big-${i}`,
      projectId: p.id,
      machineId: m.id,
      path: wt ? `/srv/wt/${p.name}/${branch.replace('/', '-')}` : `/srv/src/${p.name}`,
      kind: wt ? 'worktree' : 'main',
      branch,
      head: (i * 2654435761).toString(16).padStart(40, '0').slice(0, 40),
      headSubject: pick(TASKS).toLowerCase(),
      ahead: wt ? Math.floor(rand() * 6) : 0,
      behind: Math.floor(rand() * rand() * 30),
      dirty: rand() < 0.3 ? Math.floor(rand() * 20) : 0,
      updatedAt: now - Math.floor(rand() * 3 * D),
    });
  }

  const sessions: Session[] = [...base.sessions];
  const byMachine = new Map<string, Checkout[]>();
  for (const c of checkouts) byMachine.set(c.machineId, [...(byMachine.get(c.machineId) ?? []), c]);
  for (let i = 0; i < size.sessions; i++) {
    const m = pick(machines.slice(base.machines.length));
    const mine = byMachine.get(m.id) ?? [];
    const c = mine.length ? pick(mine) : null;
    const roll = rand();
    const liveOk = m.online;
    const status: SessionStatus = !liveOk ? 'ended' : roll < 0.07 ? 'working' : roll < 0.11 ? 'waiting' : roll < 0.25 ? 'idle' : 'ended';
    const startedAt = now - Math.floor(rand() * rand() * 10 * D) - 2 * MIN;
    const lastActivity = status === 'working' ? now - Math.floor(rand() * 20 * S) : Math.min(now - 30 * S, startedAt + Math.floor(rand() * 6 * H));
    const id = `s-big-${i.toString(36)}-${Math.floor(rand() * 1e8).toString(36)}`;
    const subs: Subagent[] = [];
    const nSubs = rand() < 0.3 ? 1 + Math.floor(rand() * 3) : 0;
    for (let k = 0; k < nSubs; k++) {
      const st = status === 'working' && k === 0 ? 'running' : rand() < 0.15 ? 'failed' : 'done';
      subs.push({
        id: `${id}-a${k}`,
        sessionId: id,
        type: pick(['Explore', 'code-reviewer', 'general-purpose']),
        description: pick(TASKS),
        status: st,
        startedAt: startedAt + k * MIN,
        endedAt: st === 'running' ? null : startedAt + (k + 3) * MIN,
        toolCalls: Math.floor(rand() * 40),
        lastText: st === 'failed' ? 'Stopped: a tool call failed three times.' : 'Finished.',
      });
    }
    const turns = 1 + Math.floor(rand() * 60);
    sessions.push({
      id,
      machineId: m.id,
      projectId: c?.projectId ?? null,
      checkoutId: c?.id ?? null,
      cwd: c?.path ?? `/home/dev/scratch-${i}`,
      title: `${pick(TASKS)}${rand() < 0.3 ? ` (${i})` : ''}`,
      status,
      pid: status === 'ended' ? null : 10_000 + i,
      model: pick(MODELS),
      permissionMode: 'default',
      startedAt,
      lastActivity,
      turns,
      toolCalls: turns * 3,
      tokens: { input: Math.floor(turns * (2_000 + rand() * 30_000)), output: Math.floor(turns * (300 + rand() * 4_000)), cacheRead: Math.floor(turns * rand() * 200_000) },
      lastText: status === 'waiting' ? 'May I run the migration against the staging database?' : 'Working through the change.',
      subagents: subs,
      workflows: [],
      origin: rand() < 0.1 ? 'orgus' : 'interactive',
    });
  }

  const pipelines: Pipeline[] = [...base.pipelines];
  for (let i = 0; i < 8; i++) {
    const p = projects[base.projects.length + i]!;
    const m = onlineMachines[(i * 5) % onlineMachines.length]!;
    pipelines.push({
      projectId: p.id,
      machineId: m.id,
      adapter: 'generic-git',
      title: 'Landing queue',
      state: i % 4 === 1 ? 'paused' : 'running',
      stages: [
        { id: 'queued', title: 'Queued' },
        { id: 'verifying', title: 'Verifying' },
        { id: 'landed', title: 'Landed' },
      ],
      items: [],
      recent: [],
      actions: ['pipeline.pause', 'pipeline.resume', 'pipeline.restart'],
      updatedAt: now,
    });
  }

  const audit: AuditEntry[] = [...base.audit];
  for (let i = 0; i < AUDIT_LIMIT - base.audit.length; i++) {
    const m = pick(machines);
    const kind = pick(KINDS);
    const failed = rand() < 0.08;
    audit.push({
      id: `a-big-${i}`,
      at: now - Math.floor((i + 1) * (7 * D / AUDIT_LIMIT) * (0.5 + rand())),
      actor: pick(ACTORS),
      action: kind,
      target: kind.startsWith('session') ? pick(TASKS) : kind.startsWith('pipeline') ? `${pick(PROJECT_NAMES)} landing queue` : `/srv/wt/${pick(PROJECT_NAMES)}`,
      machineId: m.id,
      params: {},
      result: failed ? 'error' : 'ok',
      message: failed ? pick(['The machine did not answer in time.', 'Policy refused it: pushes to main need a review.', 'It has 4 changed files. Commit or discard them first.']) : undefined,
    });
  }

  return { ...base, machines, projects, checkouts, sessions, pipelines, audit };
}

/** Keep the big fleet moving: a new session and an audit entry every few seconds. Returns a stop function. */
export function startScaleSimulator(sink: MockSink, everyMs = 4000): () => void {
  let n = 0;
  const timer = setInterval(() => {
    const st = sink.getState();
    const machines = Object.values(st.machines).filter((m) => m.online);
    const m = machines[(n * 7) % Math.max(1, machines.length)];
    if (!m) return;
    const now = Date.now();
    n++;
    const id: Id = `s-live-${now.toString(36)}`;
    const title = `${TASKS[n % TASKS.length]} (live ${n})`;
    sink.patch({
      at: now,
      upserts: {
        sessions: [
          {
            id,
            machineId: m.id,
            projectId: null,
            checkoutId: null,
            cwd: `/home/dev/live-${n}`,
            title,
            status: 'working',
            pid: 50_000 + n,
            model: MODELS[n % MODELS.length]!,
            permissionMode: 'default',
            startedAt: now,
            lastActivity: now,
            turns: 1,
            toolCalls: 0,
            tokens: { input: 0, output: 0, cacheRead: 0 },
            lastText: 'Starting.',
            subagents: [],
            workflows: [],
            origin: 'interactive',
          },
        ],
      },
      removes: {},
    });
    sink.audit({ id: `a-live-${now.toString(36)}`, at: now, actor: ACTORS[n % ACTORS.length]!, action: 'session.start', target: title, machineId: m.id, params: {}, result: 'ok' });
  }, everyMs);
  return () => clearInterval(timer);
}
